import * as React from 'react';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { PostCheckoutSurveyOption } from './types';
import { getPostCheckoutSurveyOptions, updatePostCheckoutSurveyOption } from '../api';
import { ConfirmationModal } from '../component/ConfirmationModal';
import { TextInput } from '../component/TextInput';
import { Button } from '../component/buttons/Button';
import { generateErrorMsg } from '../utils';

type PendingAction = { type: 'source'; value: string } | { type: 'bucket'; value: string };

const PPS_TOAST = { position: 'bottom-left' as const, hideProgressBar: true };

export const EditPostCheckoutSurveyOptionPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const stateOption = location.state?.option as PostCheckoutSurveyOption | undefined;

  const [option, setOption] = useState<PostCheckoutSurveyOption | undefined>(stateOption);
  const [notFound, setNotFound] = useState(false);
  const [marketingSource, setMarketingSource] = useState(stateOption?.marketing_source ?? '');
  const [marketingBucket, setMarketingBucket] = useState(stateOption?.marketing_bucket ?? '');
  const [queue, setQueue] = useState<PendingAction[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (stateOption || !id) return;
    getPostCheckoutSurveyOptions().then((options) => {
      const found = options.find((o) => o.id === id);
      if (found) {
        setOption(found);
        setMarketingSource(found.marketing_source ?? '');
        setMarketingBucket(found.marketing_bucket ?? '');
      } else {
        setNotFound(true);
      }
    });
  }, [id, stateOption]);

  if (notFound) return <Navigate to="/post-checkout-survey-options" replace />;
  if (!option) return null;

  const handleConfirmClick = () => {
    const changes: PendingAction[] = [];
    if (marketingSource !== (option.marketing_source ?? ''))
      changes.push({ type: 'source', value: marketingSource });
    if (marketingBucket !== (option.marketing_bucket ?? ''))
      changes.push({ type: 'bucket', value: marketingBucket });
    if (changes.length > 0) setQueue(changes);
  };

  const handleNext = async () => {
    if (!id || queue.length === 0) return;
    const action = queue[0];
    setIsSubmitting(true);
    try {
      const Q = 'How did you hear about Spot & Tango?';
      let message = '';
      if (action.type === 'source') {
        await updatePostCheckoutSurveyOption(id, { marketing_source: action.value });
        message = `The source for "${option.label}" was updated to "${action.value}" on post-purchase survey question "${Q}"`;
      } else if (action.type === 'bucket') {
        await updatePostCheckoutSurveyOption(id, { marketing_bucket: action.value });
        message = `The bucket for "${option.label}" was updated to "${action.value}" on post-purchase survey question "${Q}"`;
      }
      toast.info(
        <div>
          <strong>Success!</strong>
          <p>{message}</p>
        </div>,
        PPS_TOAST
      );
      const remaining = queue.slice(1);
      setQueue(remaining);
      if (remaining.length === 0) {
        navigate('/post-checkout-survey-options');
      }
    } catch (e) {
      toast.error(generateErrorMsg(e), PPS_TOAST);
      setQueue(queue.slice(1));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getModalProps = (action: PendingAction) => {
    if (action.type === 'source')
      return {
        title: `Edit source for "${option.label}" to "${action.value}"?`,
        message: 'This change can be reversed later.',
        confirmLabel: 'Confirm',
      };
    return {
      title: `Edit bucket for "${option.label}" to "${action.value}"?`,
      message: 'This change can be reversed later.',
      confirmLabel: 'Confirm',
    };
  };

  const currentAction = queue[0] ?? null;
  const modalProps = currentAction ? getModalProps(currentAction) : null;

  return (
    <div className="relative h-[calc(100vh-77px)] bg-slate50 p-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">Edit option</h1>
        <div className="border rounded-lg p-6 space-y-4">
          <div>
            <div className="font-semibold mb-1">Name</div>
            <TextInput value={option.label} disabled />
          </div>
          <div>
            <div className="font-semibold mb-1">Source</div>
            <TextInput value={marketingSource} setTextChange={setMarketingSource} />
          </div>
          <div>
            <div className="font-semibold mb-1">Bucket</div>
            <TextInput value={marketingBucket} setTextChange={setMarketingBucket} />
          </div>
        </div>
        <div className="mt-6 flex justify-center">
          <Button
            label="Confirm"
            variant="primary"
            isDisabled={isSubmitting}
            onClick={handleConfirmClick}
          />
        </div>

        {currentAction && modalProps && (
          <ConfirmationModal
            title={modalProps.title}
            message={modalProps.message}
            confirmLabel={modalProps.confirmLabel}
            onConfirm={handleNext}
            onCancel={() => setQueue([])}
            isProcessLoading={isSubmitting}
          />
        )}
      </div>
    </div>
  );
};
